import React, { useState, useEffect } from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ShieldCheck, Clock } from 'lucide-react';

interface ConsentDialogProps {
  open: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

const ConsentDialog: React.FC<ConsentDialogProps> = ({ open, onConfirm, onCancel }) => {
  const [checked, setChecked] = useState(false);
  const [now, setNow] = useState(new Date());

  // Reset checkbox when dialog opens
  useEffect(() => {
    if (open) {
      setChecked(false);
      setNow(new Date());
    }
  }, [open]);

  // Live clock
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(interval);
  }, [open]);

  return (
    <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Consent & Agreement
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="space-y-4 pt-2">
              <p className="text-sm text-muted-foreground leading-relaxed">
                I confirm that the information provided above is accurate and complete.
                I agree to the terms of shelter stay and understand this information
                will be stored digitally in accordance with organizational policy.
              </p>

              <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/50 p-3">
                <Checkbox
                  id="consent-check"
                  checked={checked}
                  onCheckedChange={(v) => setChecked(v === true)}
                />
                <Label htmlFor="consent-check" className="text-sm font-medium cursor-pointer leading-snug">
                  I have read and agree to the above terms
                </Label>
              </div>

              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  This consent will be recorded at{' '}
                  {now.toLocaleString([], {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
              </div>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel onClick={onCancel}>Cancel</AlertDialogCancel>
          <AlertDialogAction disabled={!checked} onClick={onConfirm}>
            Confirm & Submit
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default ConsentDialog;
